import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Enhanced Intersection Observer hook for lazy loading and scroll tracking
 * @param {Object} options - Configuration options
 * @returns {Object} - Observer utilities and state
 */
const useIntersectionObserver = (options = {}) => {
    const [isIntersecting, setIsIntersecting] = useState(false);
    const [hasIntersected, setHasIntersected] = useState(false);
    const [intersectionRatio, setIntersectionRatio] = useState(0);
    const [entry, setEntry] = useState(null);
    const elementRef = useRef(null);
    const observerRef = useRef(null);

    const defaultOptions = {
        threshold: [0, 0.1, 0.25, 0.5, 0.75, 1],
        rootMargin: '50px',
        triggerOnce: false,
        ...options
    };

    // Callback for intersection changes
    const handleIntersection = useCallback((entries) => {
        const [currentEntry] = entries;
        setEntry(currentEntry);
        setIsIntersecting(currentEntry.isIntersecting);
        setIntersectionRatio(currentEntry.intersectionRatio);

        // Track if element has ever been intersected (for lazy loading)
        if (currentEntry.isIntersecting && !hasIntersected) {
            setHasIntersected(true);
        }

        // If triggerOnce is true and element has intersected, disconnect observer
        if (defaultOptions.triggerOnce && currentEntry.isIntersecting && observerRef.current) {
            observerRef.current.disconnect();
        }
    }, [hasIntersected, defaultOptions.triggerOnce]);

    useEffect(() => {
        const element = elementRef.current;
        if (!element) return;

        // Create observer
        observerRef.current = new IntersectionObserver(handleIntersection, defaultOptions);
        observerRef.current.observe(element);

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [handleIntersection, defaultOptions.threshold, defaultOptions.rootMargin, defaultOptions.triggerOnce]);

    // Manual trigger function
    const trigger = useCallback(() => {
        if (elementRef.current && observerRef.current) {
            observerRef.current.unobserve(elementRef.current);
            observerRef.current.observe(elementRef.current);
        }
    }, []);

    // Disconnect observer manually
    const disconnect = useCallback(() => {
        if (observerRef.current) {
            observerRef.current.disconnect();
        }
    }, []);

    // Get element bounds
    const getBounds = useCallback(() => {
        if (elementRef.current) {
            return elementRef.current.getBoundingClientRect();
        }
        return null;
    }, []);

    return {
        elementRef,
        isIntersecting,
        hasIntersected,
        intersectionRatio,
        entry,
        trigger,
        disconnect,
        getBounds,
        // Convenience properties
        isVisible: isIntersecting,
        isFullyVisible: intersectionRatio >= 0.95,
        isPartiallyVisible: intersectionRatio > 0 && intersectionRatio < 1,
        visibilityPercentage: Math.round(intersectionRatio * 100)
    };
};

export default useIntersectionObserver;